package dev.lingvo.shared.screenshots.render

import dev.lingvo.shared.isRtl
import dev.lingvo.shared.screenshots.doc.Board
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.LayerPatch
import dev.lingvo.shared.screenshots.doc.LocaleOverride
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Role
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.geom.mirror
import dev.lingvo.shared.screenshots.geom.withBase

/** Versioned fallback prevents retained designs from changing when a newer reader renders them. */
fun effectiveLocaleOverride(page: Page, locale: String, schemaVersion: Int = 1): LocaleOverride? {
    val language = page.overrides[locale.substringBefore('-')]
    val exact = page.overrides[locale]
    if (schemaVersion < 3 || !locale.contains('-') || exact?.inheritLanguage == false) return exact ?: language
    if (language == null) return exact
    if (exact == null) return language
    val patches = language.patches.toMutableMap()
    for ((id, patch) in exact.patches) patches[id] = patches[id]?.let { mergePatch(it, patch) } ?: patch
    return exact.copy(mirror = exact.mirror ?: language.mirror, patches = patches)
}

/** Patches target displayed geometry, so mirroring happens once before locale box replacement. */
fun effectiveLayers(page: Page, board: Board, locale: String, schemaVersion: Int = 1): List<Layer> {
    val override = effectiveLocaleOverride(page, locale, schemaVersion)
    val shouldMirror = if (schemaVersion >= 3) override?.mirror ?: board.mirrorRtl else board.mirrorRtl && override?.mirror != false
    val layers = if (isRtl(locale) && shouldMirror) {
        mirror(page, board.canvas.width.toDouble() * page.slotIds.size).layers
    } else page.layers
    return layers.map { patchLayer(it, override?.patches.orEmpty()) }
}

private fun mergePatch(language: LayerPatch, exact: LayerPatch): LayerPatch = LayerPatch(
    box = exact.box ?: language.box,
    hidden = exact.hidden ?: language.hidden,
    fit = exact.fit ?: language.fit,
    family = exact.family ?: language.family,
    weight = exact.weight ?: language.weight,
    align = exact.align ?: language.align,
    lineHeight = exact.lineHeight ?: language.lineHeight,
    letterSpacingEm = exact.letterSpacingEm ?: language.letterSpacingEm,
    content = exact.content ?: language.content,
)

/** Explicit feature adoption preserves legacy locale layouts before the selected override is edited. */
fun upgradeLocaleOverrides(design: ScreenshotDesign): ScreenshotDesign {
    if (design.schemaVersion >= 3) return design
    return design.copy(schemaVersion = 3, boards = design.boards.map { board ->
        board.copy(pages = board.pages.map { page ->
            val textIds = flattened(page.layers).filterIsInstance<TextLayer>().map { it.base.id }.toSet()
            page.copy(overrides = page.overrides.mapValues { (locale, override) ->
                override.copy(
                    mirror = if (!board.mirrorRtl && override.mirror == true) false else override.mirror,
                    inheritLanguage = !locale.contains('-'),
                    patches = override.patches.mapValues { (id, patch) ->
                        if (id in textIds) patch else LayerPatch(box = patch.box, hidden = patch.hidden)
                    },
                )
            })
        })
    })
}

/** Caption identity follows the original slot layer even after a locale replaces its string binding. */
fun primaryCaptionLayer(page: Page, effective: List<Layer>, slotIndex: Int): TextLayer? {
    val candidates = flattened(page.layers).filterIsInstance<TextLayer>().filter {
        (it.content as? TextContent.SlotCaption)?.slot == slotIndex ||
            it.base.role == Role.CAPTION && it.base.roleSlot == slotIndex
    }.map { it.base.id }.toSet()
    return visibleText(effective).firstOrNull { it.base.id in candidates && it.content !is TextContent.Literal }
}

private fun flattened(layers: List<Layer>): List<Layer> = layers.flatMap {
    listOf(it) + if (it is GroupLayer) flattened(it.children) else emptyList()
}

private fun visibleText(layers: List<Layer>): List<TextLayer> = layers.flatMap {
    when {
        it.base.hidden || it.base.opacity <= 0 -> emptyList()
        it is TextLayer -> listOf(it)
        it is GroupLayer -> visibleText(it.children)
        else -> emptyList()
    }
}

private fun patchLayer(layer: Layer, patches: Map<String, LayerPatch>): Layer {
    val patch = patches[layer.base.id]
    var result = if (patch == null) layer else layer.withBase(layer.base.copy(
        box = patch.box ?: layer.base.box, hidden = patch.hidden ?: layer.base.hidden,
    ))
    if (result is TextLayer && patch != null) result = result.copy(
        content = patch.content ?: result.content,
        fit = patch.fit ?: result.fit,
        style = result.style.copy(family = patch.family ?: result.style.family, weight = patch.weight ?: result.style.weight,
            align = patch.align ?: result.style.align, lineHeight = patch.lineHeight ?: result.style.lineHeight,
            letterSpacingEm = patch.letterSpacingEm ?: result.style.letterSpacingEm),
    )
    if (result is GroupLayer) result = result.copy(children = result.children.map { patchLayer(it, patches) })
    return result
}
